import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Grid,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { IdentityProfile } from '../../app/types';
import { fileToBase64 } from '../../lib/files';
import PageSection from '../shared/PageSection';

interface IdentityProfilesPageProps {
  readonly tenantId: string;
  readonly profiles: IdentityProfile[];
  readonly onCreateProfile: (payload: {
    tenantId: string;
    idNumber: string;
    name: string;
    surname: string;
    phoneNumber: string;
  }) => Promise<void>;
  readonly onUpdateProfile: (
    profileId: string,
    payload: { tenantId: string; idNumber?: string; name?: string; surname?: string; phoneNumber?: string }
  ) => Promise<void>;
  readonly onDeleteProfile: (profileId: string, tenantId: string) => Promise<void>;
  readonly onUploadReferencePhoto: (
    profileId: string,
    payload: { tenantId: string; sourceImageName: string; imageBase64: string }
  ) => Promise<void>;
}

const PAGE_SIZE = 5;

export default function IdentityProfilesPage({
  tenantId,
  profiles,
  onCreateProfile,
  onUpdateProfile,
  onDeleteProfile,
  onUploadReferencePhoto,
}: IdentityProfilesPageProps) {
  const [idNumber, setIdNumber] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [editingProfileId, setEditingProfileId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [uploadingProfileId, setUploadingProfileId] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredProfiles = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return profiles.filter((profile) => {
      if (statusFilter !== 'all' && profile.status !== statusFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [profile.name, profile.surname, profile.idNumber, profile.phoneNumber].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [profiles, search, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredProfiles.length / PAGE_SIZE));
  const visibleProfiles = filteredProfiles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetForm = () => {
    setIdNumber('');
    setName('');
    setSurname('');
    setPhoneNumber('');
    setEditingProfileId('');
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingProfileId) {
        await onUpdateProfile(editingProfileId, {
          tenantId,
          idNumber,
          name,
          surname,
          phoneNumber,
        });
      } else {
        await onCreateProfile({
          tenantId,
          idNumber,
          name,
          surname,
          phoneNumber,
        });
      }
      resetForm();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to save identity profile.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (profile: IdentityProfile) => {
    setEditingProfileId(profile.profileId);
    setIdNumber(profile.idNumber);
    setName(profile.name);
    setSurname(profile.surname);
    setPhoneNumber(profile.phoneNumber);
  };

  const removeProfile = async (profileId: string) => {
    try {
      setError('');
      await onDeleteProfile(profileId, tenantId);
      if (editingProfileId === profileId) {
        resetForm();
      }
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete identity profile.');
    }
  };

  const uploadReference = async (profileId: string, files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    try {
      setUploadingProfileId(profileId);
      setError('');
      const imageBase64 = await fileToBase64(file);
      await onUploadReferencePhoto(profileId, {
        tenantId,
        sourceImageName: file.name,
        imageBase64,
      });
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Unable to upload reference photo.');
    } finally {
      setUploadingProfileId('');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <PageSection title="Identity Profiles" subtitle="Tenant-scoped people records used for 1:1 face verification by ID number.">
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Name, ID number, phone"
                fullWidth
              />
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="missing-reference-photo">Missing Reference</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="invalid-reference-photo">Invalid Reference</MenuItem>
              </TextField>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Identity</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleProfiles.map((profile) => (
                  <TableRow key={profile.profileId}>
                    <TableCell>
                      <strong>{profile.name} {profile.surname}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        ID: {maskIdNumber(profile.idNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell>{maskPhoneNumber(profile.phoneNumber)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={profile.status} color={profile.status === 'ready' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Faces: {profile.referenceFaceCount}
                        </Typography>
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          disabled={uploadingProfileId === profile.profileId}
                        >
                          {uploadingProfileId === profile.profileId ? 'Uploading...' : profile.referenceImageKey ? 'Replace Photo' : 'Upload Photo'}
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={(event) => void uploadReference(profile.profileId, event.target.files)}
                          />
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>{new Date(profile.createdAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="text" onClick={() => startEdit(profile)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" variant="text" onClick={() => void removeProfile(profile.profileId)}>
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!visibleProfiles.length ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">No identity profiles match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Showing {visibleProfiles.length} of {filteredProfiles.length} profiles
              </Typography>
              <Pagination page={page} count={pageCount} onChange={(_, value) => setPage(value)} size="small" />
            </Stack>
          </Stack>
        </PageSection>
      </Grid>

      <Grid item xs={12} lg={4}>
        <PageSection
          title={editingProfileId ? 'Edit Identity Profile' : 'Create Identity Profile'}
          subtitle="Register a person before uploading a reference face image."
        >
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField label="ID Number" value={idNumber} onChange={(event) => setIdNumber(event.target.value)} />
            <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} />
            <TextField label="Surname" value={surname} onChange={(event) => setSurname(event.target.value)} />
            <TextField label="Phone Number" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => void submit()}
                disabled={!idNumber || !name || !surname || !phoneNumber || saving}
              >
                {saving ? 'Saving...' : editingProfileId ? 'Save Changes' : 'Create Profile'}
              </Button>
              {editingProfileId ? (
                <Button variant="outlined" onClick={resetForm} disabled={saving}>
                  Cancel
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </PageSection>
      </Grid>
    </Grid>
  );
}

function maskIdNumber(value: string): string {
  if (value.length <= 4) {
    return value;
  }

  return `${'*'.repeat(Math.max(0, value.length - 4))}${value.slice(-4)}`;
}

function maskPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 4) {
    return value;
  }

  return `${'*'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
}
